import { useMemo } from 'react';
import { useNavigate, useQueryState, useMutation, useQuery, useI18n } from '@ywwwtseng/react-kit';
import {
  Typography,
} from '@ywwwtseng/tma-react-kit';
import * as schema from '@/db/schema';
import { type FormElement, Form, validator, useForm } from '@/lib/react-form';
import type { Gift } from '@/types';

export default function ClaimGiftScreen({
  params,
}: {
  params: { gift_id: string };
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const me = useQueryState<typeof schema.users.$inferSelect>('me');
  const { mutate, isLoading } = useMutation('gift:claim');
  const { data: gift } = useQuery<Gift>('gifts', {
    params: {
      id: params.gift_id,
    },
  });

  const form = useForm({
    values: {
      default: {
        claim_code: '',
      },
    },
  });

  const elements = useMemo<{ row: FormElement[] }[]>(() => {
    return [
      {
        row: [
          {
            ui: 'text',
            name: 'claim_code',
            validate: validator.required({
              message: t('validation.required', {
                name: t('claim_gift.code'),
              }),
            }),
            props: {
              placeholder: t('claim_gift.code_placeholder'),
            },
          },
        ],
      },
    ];
  }, [form.values]);

  if (!gift) {
    return null;
  }

  return (
    <div className="flex flex-col justify-start w-full h-full gap-2 py-3 px-5">
      {gift.image && (
        <img
          src={gift.image}
          alt={gift.description}
          className="bg-card object-contain w-full aspect-640/360 rounded-lg overflow-hidden"
        />
      )}

      <div className="flex flex-col justify-start w-full flex-1 gap-2">
        <Typography whitespacePreWrap overflowWrap="break-word" size="3" weight={600}>
          {gift.description}
        </Typography>

        <Form
          name="deposit"
          isLoading={isLoading}
          {...form}
          elements={elements}
          submit={{
            label: t('common.claim'),
          }}
          onSubmit={(values) => {
            void mutate({
              type: 'claim_code',
              gift_id: params.gift_id,
              claim_code: values.claim_code,
              language_code: me?.language_code,
            }).then((result) => {
              if (result.ok) {
                navigate('Home');
              }
            });
          }}
        />
      </div>
    </div>
  );
}
